import CssBaseline from '@mui/material/CssBaseline';
import { FlintThemeProvider } from '@wiley-flint/theme';
import { AuthProvider } from 'oidc-react';
import { RouterProvider } from 'react-router-dom';
import { FORBIDDEN_PATH, ORIGINAL_PATH, UNAUTHORIZED_PATH, LOGOUT_PATH } from './constants';
import env from './env';
import router from './router';
import { BypassAuthProvider } from './bypass/BypassAuth';
import bypassedRoutes from './bypassed-routes';
import { STATIC_ROUTES } from './constants';
import { AppConfigProvider } from './provider/AppConfigProvider';
import { removeAuthCookie } from './utils/authCookie';

// Paths that should not be saved as the return path after login
const EXCLUDED_REDIRECT_PATHS = [
  LOGOUT_PATH,
  FORBIDDEN_PATH,
  UNAUTHORIZED_PATH,
  env.OIDC_REDIRECT_URI,
  env.OIDC_REFRESH_URI,
];

function App() {
  const theme = localStorage.getItem('qap-theme-override') ?? 'default';
  // Helper function to check if a path should be excluded from redirect
  const isExcludedPath = (pathname: string): boolean => {
    return EXCLUDED_REDIRECT_PATHS.some(excludedPath => pathname === excludedPath);
  };

  // Helper function to get a safe redirect path (defaults to home if path is excluded or invalid)
  const getSafeRedirectPath = (path: string | null): string => {
    if (!path) {
      return '/';
    }
    try {
      const url = new URL(path, window.location.origin);
      if (isExcludedPath(url.pathname)) {
        return '/';
      }
      return path;
    } catch {
      // If path parsing fails, redirect to home
      return '/';
    }
  };

  const oidcConfig = {
    onBeforeSignIn: () => {
      // remove the old qap cookie if it exists (must match set options or it survives)
      removeAuthCookie();

      // Only save the current path if it's not an excluded path
      if (!isExcludedPath(window.location.pathname)) {
        localStorage.setItem(ORIGINAL_PATH, window.location.href);
      }
      return window.location.href;
    },
    onSignIn: () => {
      const path = localStorage.getItem(ORIGINAL_PATH);
      const safePath = getSafeRedirectPath(path);
      localStorage.removeItem(ORIGINAL_PATH); // Clean up after use
      window.location.replace(safePath);
    },
    automaticSilentRenew: true,
    authority: env.OIDC_AUTHORITY,
    clientId: env.OIDC_CLIENT_ID,
    clientSecret: env.OIDC_CLIENT_SECRET,
    redirectUri: `${window.location.origin}${env.OIDC_REDIRECT_URI}`,
    responseType: 'code',
    scopes: 'openid',
  };

  return (
    <FlintThemeProvider variant={theme} type="light">
      <CssBaseline />
      {
        window.location.pathname.startsWith(STATIC_ROUTES) ?
          <AppConfigProvider>
            <BypassAuthProvider >
              <RouterProvider router={bypassedRoutes} />
            </BypassAuthProvider>
          </AppConfigProvider> :
          <AuthProvider {...oidcConfig}>
            <AppConfigProvider>
              <RouterProvider router={router} />
            </AppConfigProvider>
          </AuthProvider>
      }
    </FlintThemeProvider>
  );
}

export default App;
